import { debounce } from 'lodash';
import { Edge, Viewport } from 'reactflow'; // Import Edge and Viewport
import { FlowState } from './useStore';
import { CustomNode } from '../types'; // Remove CustomEdge import
import { UnifiedSchema } from '../types/schema'; // Import UnifiedSchema

const FLOW_STORAGE_KEY = 'mr-architect-flow-state';
const PROJECT_STORAGE_KEY = 'mr-architect-current-project-id';
const SCHEMA_EDITOR_STORAGE_KEY = 'mr-architect-schema-editor-state'; // New key for schema editor
const DEBOUNCE_WAIT = 1000; // 1 second

interface PersistedSchemaEditorState {
  unifiedSchema?: UnifiedSchema | null;
  timestamp: number;
  version: string;
}

interface PersistedState {
  nodes: CustomNode[];
  edges: Edge[]; // Use Edge[] type here
  viewport?: Viewport;
  timestamp: number;
  version: string;
}

/**
 * Saves the current project ID to localStorage
 */
export const saveProjectId = (projectId: string | null) => {
  try {
    if (projectId) {
      localStorage.setItem(PROJECT_STORAGE_KEY, projectId);
    } else {
      localStorage.removeItem(PROJECT_STORAGE_KEY);
    }
  } catch (error) {
  }
};

/**
 * Loads the persisted project ID from localStorage
 */
export const loadProjectId = (): string | null => {
  try {
    const savedProjectId = localStorage.getItem(PROJECT_STORAGE_KEY);
    if (!savedProjectId) {
      return null;
    }
    return savedProjectId;
  } catch (error) {
    return null;
  }
};

// Current version of the state schema
const CURRENT_VERSION = '1.0.0';

/**
 * Validates the structure of loaded schema editor state
 */
const isValidPersistedSchemaEditorState = (state: any): state is PersistedSchemaEditorState => {
  return (
    state &&
    (state.unifiedSchema === undefined || state.unifiedSchema === null || typeof state.unifiedSchema === 'object') &&
    typeof state.timestamp === 'number' &&
    typeof state.version === 'string'
  );
};
/**
 * Validates the structure of loaded state
 */
const isValidPersistedState = (state: any): state is PersistedState => {
  return (
    state &&
    Array.isArray(state.nodes) &&
    Array.isArray(state.edges) &&
    typeof state.timestamp === 'number' &&
    typeof state.version === 'string'
  );
};

/**
 * Handles version migrations if needed
 */
const migrateState = (state: PersistedState): PersistedState => {
  // For now just return the state as is
  // In the future, implement version-specific migrations here
  return state;
};

/**
 * Validates loaded nodes, removing invalid parentId references.
 */
const validatePersistedNodes = (nodes: CustomNode[]): CustomNode[] => {
  if (!Array.isArray(nodes) || nodes.length === 0) {
    return []; // Return empty array if input is invalid or empty
  }

  const nodeIds = new Set(nodes.map(n => n.id));
  const sanitizedNodes: CustomNode[] = [];
  let changesMade = false;

  for (const node of nodes) {
    if (node.parentId && !nodeIds.has(node.parentId)) {
      console.warn(`[persistence] Node "${node.data.label || node.id}" had invalid parentId "${node.parentId}". Removing reference.`);
      // Create a new node object excluding parentId and extent
      const { parentId, extent, ...restOfNode } = node;
      sanitizedNodes.push(restOfNode as CustomNode); // Type assertion might be needed depending on CustomNode structure
      changesMade = true;
    } else {
      sanitizedNodes.push(node);
    }
  }

  // Return the original array if no changes were needed, otherwise the new one
  return changesMade ? sanitizedNodes : nodes;
};


/**
 * Saves the current state to localStorage
 */
export const saveState = (state: Partial<FlowState>) => {
  try {
    const persistedState: PersistedState = {
      nodes: state.nodes || [],
      edges: state.edges || [],
      viewport: state.viewport,
      timestamp: Date.now(),
      version: CURRENT_VERSION,
    };

    localStorage.setItem(FLOW_STORAGE_KEY, JSON.stringify(persistedState));
  } catch (error) {
  }
};

/**
 * Debounced version of saveState to prevent too frequent saves
 */
export const debouncedSaveState = debounce(saveState, DEBOUNCE_WAIT);

/**
 * Loads the persisted state from localStorage
 */
export const loadState = (): Partial<FlowState> | null => {
  try {
    const savedState = localStorage.getItem(FLOW_STORAGE_KEY);
    if (!savedState) {
      return null;
    }

    const parsedState = JSON.parse(savedState);
    if (!isValidPersistedState(parsedState)) {
      console.error('[persistence] Invalid state structure');
      return null;
    }

    const migratedState = migrateState(parsedState);

    // Validate and sanitize nodes before returning
    const validatedNodes = validatePersistedNodes(migratedState.nodes);

    return {
      nodes: validatedNodes, // Use the validated nodes
      edges: migratedState.edges,
      viewport: migratedState.viewport,
    };
  } catch (error) {
    console.error('[persistence] Failed to load state:', error);
    return null;
  }
};

/**
 * Clears the persisted state
 */
export const clearState = () => {
  try {
    localStorage.removeItem(FLOW_STORAGE_KEY);
    localStorage.removeItem(PROJECT_STORAGE_KEY);
  } catch (error) {
  }
};

/**
 * Saves the current schema editor state to localStorage
 * Now uses schema ID as part of the storage key to support multiple schemas
 */
export const saveStateSchemaEditor = (unifiedSchema: UnifiedSchema | null) => {
  try {
    if (!unifiedSchema) return;
    
    const schemaId = unifiedSchema.id;
    if (!schemaId) return;
    
    const persistedState: PersistedSchemaEditorState = {
      unifiedSchema: unifiedSchema,
      timestamp: Date.now(),
      version: CURRENT_VERSION,
    };

    const storageKey = `${SCHEMA_EDITOR_STORAGE_KEY}-${schemaId}`;
    localStorage.setItem(storageKey, JSON.stringify(persistedState));
  } catch (error) {
  }
};

/**
 * Debounced version of saveStateSchemaEditor to prevent too frequent saves
 */
export const debouncedSaveStateSchemaEditor = debounce(saveStateSchemaEditor, DEBOUNCE_WAIT);

/**
 * Loads the persisted schema editor state from localStorage for a specific schema ID
 */
export const loadStateSchemaEditor = (schemaId?: string): PersistedSchemaEditorState | null => {
  try {
    // If no schemaId is provided, return null
    if (!schemaId) return null;
    
    const storageKey = `${SCHEMA_EDITOR_STORAGE_KEY}-${schemaId}`;
    const savedState = localStorage.getItem(storageKey);
    if (!savedState) {
      return null;
    }

    const parsedState = JSON.parse(savedState);
    if (!isValidPersistedSchemaEditorState(parsedState)) {
      console.error('[persistence] Invalid schema editor state structure');
      return null;
    }

    // No migration needed for schema editor state yet, but can be added here
    return parsedState;
  } catch (error) {
    console.error('[persistence] Failed to load schema editor state:', error);
    return null;
  }
};

/**
 * Clears the persisted schema editor state for a specific schema ID
 * If no schemaId is provided, it will clear all schema editor states
 */
export const clearStateSchemaEditor = (schemaId?: string) => {
  try {
    if (schemaId) {
      // Clear only the specific schema state
      const storageKey = `${SCHEMA_EDITOR_STORAGE_KEY}-${schemaId}`;
      localStorage.removeItem(storageKey);
    } else {
      // Clear all schema editor states by finding all keys that start with the prefix
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(SCHEMA_EDITOR_STORAGE_KEY)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    }
  } catch (error) {
  }
};

/**
 * Hook to automatically save state changes
 */
export const useStatePersistence = (state: Partial<FlowState>): void => { // This hook will now only handle flow state
  if (state.nodes?.length || state.edges?.length || state.viewport) {
    debouncedSaveState(state);
  }
};

/**
 * Hook to automatically save schema editor state changes
 */
export const useStatePersistenceSchemaEditor = (unifiedSchema: UnifiedSchema | null): void => {
  if (unifiedSchema) {
    debouncedSaveStateSchemaEditor(unifiedSchema);
  }
};
